import { useNavigate } from "react-router-dom"
import { LiteratureReviewProvider, useLiteratureReview } from "../../Context/LiteratureReviewContext"
import { LiteratureReviewReportProvider } from "../../Context/LiteratureReviewReportContext"
import { Card } from "../../Components/Card/Card"
import RecentCard from "../../Components/RecentCard/RecentCard"
import UpgradeSubscriptionModal from "../../Components/UpgradeSubscriptionModal/UpgradeSubscriptionModal"
import PaperSources from "../PaperSources/PaperSources"
import AbstractScreening from "../AbstractScreening/AbstractScreening"
import EvaluateScreening from "../EvaluateScreening/EvaluateScreening"
import FullTextScreening from "../FullTextScreening/FullTextScreening"
import ExtractionDefiniton from "../ExtractionDefinition/ExtractionDefinition"
import ExtractionResults from "../ExtractionResults/ExtractionResults"
import ReportGeneration from "../ReportGeneration/ReportGeneration"
import "../Screening/Screening.css"
import "./LiteratureReviewTools.css"

const LiteratureReviewAccessGate = ({
    user,
    hasEnterprise,
    isEnterpriseActive,
    onLogin,
    onExplore,
}) => (
    <div className="screening-container">
        <div className="premium-feature-card">
            <div className="premium-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="#FFD700"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
            <h2>📚 MedRPT Systematic Review Protocol</h2>
            <p className="premium-description">
                Accelerate your systematic literature review with MedRPT&apos;s advanced AI-powered platform.
                Perform abstract screening, full-text screening, structured data extraction, and critical
                appraisal using evidence-based methodologies and customizable inclusion/exclusion criteria
                for rigorous academic research.
            </p>

            <div className="feature-highlights">
                <div className="feature-item">
                    <span className="feature-icon">🔬</span>
                    <span>Evidence-based workflow aligned with PRISMA guidelines</span>
                </div>
                <div className="feature-item">
                    <span className="feature-icon">⚡</span>
                    <span>High-throughput processing of thousands of research articles</span>
                </div>
                <div className="feature-item">
                    <span className="feature-icon">🎯</span>
                    <span>Precision AI algorithms trained on medical literature databases</span>
                </div>
                <div className="feature-item">
                    <span className="feature-icon">📊</span>
                    <span>Comprehensive reporting with PRISMA flow diagram data</span>
                </div>
                <div className="feature-item">
                    <span className="feature-icon">🔄</span>
                    <span>Real-time progress tracking with detailed screening statistics</span>
                </div>
                <div className="feature-item">
                    <span className="feature-icon">📋</span>
                    <span>Exportable results for systematic review documentation</span>
                </div>
            </div>

            <div className="cta-buttons lrt-cta-buttons">
                {!user ? (
                    <button type="button" onClick={onLogin} className="lrt-cta-button lrt-primary-button">
                        Start Your Systematic Review
                    </button>
                ) : hasEnterprise && !isEnterpriseActive ? (
                    <div className="enterprise-expired-message">
                        Enterprise Subscription Expired. Please contact your administrator.
                    </div>
                ) : (
                    <a
                        href="https://www.medrpt.ai/contact-us"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lrt-cta-button lrt-contact-us-button"
                    >
                        <span className="lrt-contact-us-button-text">Contact Us</span>
                        <svg
                            className="lrt-contact-us-icon"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path
                                d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </a>
                )}
                <button type="button" onClick={onExplore} className="lrt-cta-button lrt-explore-button">
                    Explore MedRPT Features
                </button>
            </div>
        </div>
    </div>
)

/**
 * Inner component: all state and actions come from LiteratureReviewContext.
 * Kept separate so the provider wraps it and all child step pages share context.
 */
const LiteratureReviewContent = () => {
    const navigate = useNavigate()
    const {
        id,
        step,
        user,
        isAdvanced,
        isEnterpriseActive,
        hasEnterprise,
        loading,
        errorMessage,
        handleNavigateToPaperSources,
        closeErrorModal,
    } = useLiteratureReview()

    const hasLitReviewAccess = user && (isEnterpriseActive || isAdvanced)

    if (loading) {
        return (
            <div className="screening-loading">
                <p>Loading...</p>
            </div>
        )
    }

    if (!hasLitReviewAccess) {
        return (
            <LiteratureReviewAccessGate
                user={user}
                hasEnterprise={hasEnterprise}
                isEnterpriseActive={isEnterpriseActive}
                onLogin={() => navigate("/login")}
                onExplore={() => navigate("/")}
            />
        )
    }

    if (id) {
        const withReportNav = (element) => (
            <LiteratureReviewReportProvider reviewId={id}>{element}</LiteratureReviewReportProvider>
        )
        if (step === "abstract-screening") return withReportNav(<AbstractScreening />)
        if (step === "recommended-abstracts") return withReportNav(<EvaluateScreening />)
        if (step === "full-text-screening") return withReportNav(<FullTextScreening />)
        if (step === "data-extraction-definition") return withReportNav(<ExtractionDefiniton />)
        if (step === "data-extraction-results") return withReportNav(<ExtractionResults />)
        if (step === "research-report") return withReportNav(<ReportGeneration />)
        return withReportNav(<PaperSources />)
    }

    return (
        <div>
            <Card search={handleNavigateToPaperSources} />
            <RecentCard search={handleNavigateToPaperSources} />
            {errorMessage && (
                <UpgradeSubscriptionModal message={errorMessage} onClose={closeErrorModal} />
            )}
        </div>
    )
}

/**
 * Outer wrapper: mounts LiteratureReviewProvider so all child step pages
 * can access shared context via useLiteratureReview().
 */
const LiteratureReviewTools = () => (
    <LiteratureReviewProvider>
        <LiteratureReviewContent />
    </LiteratureReviewProvider>
)

export default LiteratureReviewTools
